// Playlists need an account (user ruling, Sep 2026: "I think playlists should
// only be available when you're signed in… I expect a playlist I save on one
// machine to be visible on others signed in with the same account").
//
// Signed in with access, every playlist is kept in your account and follows
// you to every device (src/data/sharedPlaylistSync.ts). Signed out, there are
// no playlists: the Playlists page offers sign-in, and every other place that
// lists or adds to playlists shows nothing. The playlists a device already
// holds stay stored, and come back when their account signs in again.
//
// A build with no accounts at all (unconfigured, e.g. the e2e build) keeps
// playlists on the device, as the app always has: there is no account for
// them to belong to. While the sign-in check is still running, a device whose
// playlists belong to an account shows them, so a reload does not flash an
// empty list; a device that turns out to be signed out hides them a moment
// later. A non-component module, so fast-refresh lint stays clean.

import { getAuthState, useAuth, type AuthStatus } from '@/auth/session';
import { useStore } from '@/state/store';
import type { PlaylistDoc } from '@/storage/playlists';

/** Can this viewer have playlists? */
export function playlistsAvailableFor(status: AuthStatus, playlistAccount: string | null): boolean {
  return (
    status === 'unconfigured' ||
    status === 'active' ||
    (status === 'loading' && playlistAccount !== null)
  );
}

/** The same, outside React (the command layer). */
export function playlistsAvailable(): boolean {
  return playlistsAvailableFor(getAuthState().status, useStore.getState().playlistAccount);
}

export function usePlaylistsAvailable(): boolean {
  const { status } = useAuth();
  const account = useStore((s) => s.playlistAccount);
  return playlistsAvailableFor(status, account);
}

const NONE: PlaylistDoc[] = [];

/** The viewer's playlists: none while playlists are not available to them. */
export function usePlaylists(): PlaylistDoc[] {
  const available = usePlaylistsAvailable();
  const all = useStore((s) => s.userPlaylists);
  return available ? all : NONE;
}
